import { useMemo, useState, useCallback } from 'react';
import type { Transcript, TranscriptEntry } from './SearchTranscript.types';
import FRSPhaseLog from './FRSPhaseLog';

interface SearchTranscriptLogProps {
  transcript: Transcript;
  kbName: string | null;
  showHelp: boolean;
  hidden: boolean;
}

export default function SearchTranscriptLog({ transcript, kbName, showHelp, hidden }: SearchTranscriptLogProps) {
  const [collapsedSteps, setCollapsedSteps] = useState<Set<number>>(() => new Set([1, 2, 3, 4]));
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());

  const togglePhase = useCallback((phase: string) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      if (next.has(phase)) next.delete(phase); else next.add(phase);
      return next;
    });
  }, []);

  const toggleCollapsed = useCallback((stepNumber: number) => {
    setCollapsedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepNumber)) next.delete(stepNumber); else next.add(stepNumber);
      return next;
    });
  }, []);

  const isFRSTranscript = useMemo(() =>
    (transcript.entries ?? []).some(e =>
      e.function_name === 'pass1_batch_a' || e.function_name === 'relevance_assessment'
    ),
  [transcript.entries]);

  const isM5AEntry = useCallback((entry: TranscriptEntry) => {
    const fn = entry.function_name || '';
    return fn.startsWith('Step M5A:') || fn.includes('SEMANTIC QUERY - Retrieval of ranked list of vectors');
  }, []);

  const isM5BEntry = useCallback((entry: TranscriptEntry) => {
    const fn = entry.function_name || '';
    return fn.startsWith('Step M5B:') || fn.includes('KEYWORDS-BASED QUERY - Retrieval of ranked list of documents');
  }, []);

  const renderM5AOutputsMatrix = useCallback((entry: TranscriptEntry) => {
    const matrixRaw = (entry.outputs || {})['matrix_4x11'];
    if (!matrixRaw || typeof matrixRaw !== 'object') return null;

    const matrixObj = matrixRaw as Record<string, unknown>;
    const columns = Object.entries(matrixObj)
      .map(([key, value]) => ({
        key, value,
        colIndex: (() => { const m = key.match(/^Col_(\d+)_/); return m ? Number(m[1]) : Number.POSITIVE_INFINITY; })(),
      }))
      .filter(c => Array.isArray(c.value))
      .sort((a, b) => a.colIndex - b.colIndex);

    if (columns.length === 0) return null;
    const columnValues = columns.map(c => (c.value as unknown[]).map(v => String(v ?? '')));
    const rows = [0, 1, 2, 3].map(rowIndex => columnValues.map(col => col[rowIndex] ?? ''));

    return (
      <div className="m5a-outputs">
        <h4>Outputs</h4>
        <div className="matrix-wrapper">
          <table className="matrix-table">
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} className={cIdx === 0 ? 'matrix-header-cell' : undefined}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, []);

  const renderM5BOutputsMatrix = useCallback((entry: TranscriptEntry) => {
    const matrixRaw = (entry.outputs || {})['matrix_4x11'];
    if (!matrixRaw || typeof matrixRaw !== 'object') return null;

    const matrixObj = matrixRaw as Record<string, unknown>;
    const columns = Object.entries(matrixObj)
      .map(([key, value]) => ({
        key, value,
        colIndex: (() => { const m = key.match(/^Col_(\d+)_/); return m ? Number(m[1]) : Number.POSITIVE_INFINITY; })(),
      }))
      .filter(c => Array.isArray(c.value))
      .sort((a, b) => a.colIndex - b.colIndex);

    if (columns.length === 0) return null;
    const columnValues = columns.map(c => (c.value as unknown[]).map(v => String(v ?? '')));
    const rows = [0, 1, 2, 3].map(rowIndex => columnValues.map(col => col[rowIndex] ?? ''));

    return (
      <div className="m5b-outputs">
        <h4>Outputs</h4>
        <div className="matrix-wrapper">
          <table className="matrix-table">
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} className={cIdx === 0 ? 'matrix-header-cell' : undefined}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, []);

  const renderM5AInputs = useCallback((entry: TranscriptEntry) => {
    const lines = (entry.description || '').split('\n').map(l => l.trim()).filter(Boolean);
    const bulletLines = lines
      .filter(l => l.startsWith('•'))
      .map(l => l.toLowerCase().includes('knowledge base:') ? `• Knowledge base: ${kbName ?? transcript.kb_id ?? ''}` : l);
    const otherLines = lines.filter(l => !l.startsWith('•'));

    return (
      <div className="m5a-inputs">
        <div className="m5a-inputs-title">Inputs:</div>
        {bulletLines.length > 0 && (
          <ul className="m5a-bullets">
            {bulletLines.map((b, idx) => <li key={idx}>{b.replace(/^•\s*/, '')}</li>)}
          </ul>
        )}
        {otherLines.length > 0 && (
          <div className="m5a-lines">
            {otherLines.map((l, idx) => <div key={idx} className="m5a-line">{l}</div>)}
          </div>
        )}
      </div>
    );
  }, [kbName, transcript.kb_id]);

  const renderM5BInputs = useCallback((entry: TranscriptEntry) => {
    const lines = (entry.description || '').split('\n').map(l => l.trim()).filter(Boolean);
    const bulletLines = lines
      .filter(l => l.startsWith('•'))
      .map(l => l.toLowerCase().includes('knowledge base:') ? `• Knowledge base: ${kbName ?? transcript.kb_id ?? ''}` : l);
    const otherLines = lines.filter(l => !l.startsWith('•'));

    return (
      <div className="m5b-inputs">
        <div className="m5b-inputs-title">Inputs:</div>
        {bulletLines.length > 0 && (
          <ul className="m5b-bullets">
            {bulletLines.map((b, idx) => <li key={idx}>{b.replace(/^•\s*/, '')}</li>)}
          </ul>
        )}
        {otherLines.length > 0 && (
          <div className="m5b-lines">
            {otherLines.map((l, idx) => <div key={idx} className="m5b-line">{l}</div>)}
          </div>
        )}
      </div>
    );
  }, [kbName, transcript.kb_id]);

  const m5aTitle = useMemo(() => 'SEMANTIC QUERY - Retrieval of ranked list of vectors', []);
  const m5bTitle = useMemo(() => 'KEYWORDS-BASED QUERY - Retrieval of ranked list of documents', []);

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3,
    });
  };

  const renderObject = (obj: Record<string, unknown>, indent = 0) => {
    if (!obj || Object.keys(obj).length === 0) return <span className="empty-value">—</span>;
    return (
      <div className="object-content" style={{ marginLeft: indent * 16 }}>
        {Object.entries(obj).map(([key, value]) => (
          <div key={key} className="object-row">
            <span className="object-key">{key}:</span>
            <span className="object-value">
              {value === null ? 'null' :
               value === undefined ? 'undefined' :
               typeof value === 'boolean' ? (value ? '✔ true' : '✗ false') :
               typeof value === 'number' ? value.toString() :
               typeof value === 'string' ? `"${value}"` :
               Array.isArray(value) ? `[${value.slice(0, 5).join(', ')}${value.length > 5 ? ', ...' : ''}] (${value.length} items)` :
               typeof value === 'object' ? JSON.stringify(value).slice(0, 100) :
               String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <main className="transcript-main" style={{ display: hidden ? 'none' : 'block' }}>
      {showHelp && (
        <div className="help-panel" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ marginTop: 0 }}>🔬 Search Execution Guide</h2>
          <p>This transcript shows the step-by-step execution of your search query. Each step represents a function call in the search pipeline.</p>
          <h3>📋 Key Search Steps</h3>
          <ul style={{ lineHeight: '1.8' }}>
            <li><strong>Query Processing:</strong> Your natural language query is analyzed and converted into optimized search tokens (keywords)</li>
            <li><strong>Vector Retrieval (M5A):</strong> Semantic search using embeddings to find conceptually similar content</li>
            <li><strong>BM25 Retrieval (M5B):</strong> Keyword-based search using term frequency analysis</li>
            <li><strong>Fusion (M6):</strong> Combines results from both retrieval methods using weighted scoring</li>
            <li><strong>Reranking (M7):</strong> Cross-encoder model refines the ranking for better relevance</li>
            <li><strong>Result Processing:</strong> Final results are formatted and returned with metadata</li>
          </ul>
          <h3>💡 Understanding the Transcript</h3>
          <ul style={{ lineHeight: '1.8' }}>
            <li><strong>Inputs:</strong> What data was passed into each function</li>
            <li><strong>Outputs:</strong> What the function returned (e.g., number of chunks found)</li>
            <li><strong>Duration:</strong> How long each step took in milliseconds</li>
            <li><strong>Notes:</strong> Additional context or warnings about the step</li>
          </ul>
          <p style={{ marginBottom: 0, fontSize: '0.9em', color: '#6c757d' }}>
            💾 Use the "Save Transcript" button to export this execution log for debugging or analysis.
          </p>
        </div>
      )}

      {isFRSTranscript
        ? <FRSPhaseLog entries={transcript.entries} expanded={expandedPhases} onToggle={togglePhase} formatTime={formatTime} isRunning={transcript.status === 'running'} />
        : (
          <div className="entries-list">
            {transcript.entries.map((entry, index) => {
              const collapsible = entry.step_number >= 1 && entry.step_number <= 4;
              const collapsed = collapsible && collapsedSteps.has(entry.step_number);
              const isM5A = isM5AEntry(entry);
              const isM5B = isM5BEntry(entry);

              if (isM5A) {
                const m5aMatrix = renderM5AOutputsMatrix(entry);
                return (
                  <div key={index} className="entry-card entry-m5a">
                    <div className="entry-header">
                      <span className="entry-step">Step M5A</span>
                      <span className="entry-time">{formatTime(entry.timestamp)}</span>
                      {entry.duration_ms !== undefined && entry.duration_ms !== null && (
                        <span className="entry-duration">{entry.duration_ms.toFixed(1)}ms</span>
                      )}
                      <span className="entry-function-inline"><code>{m5aTitle}</code></span>
                    </div>
                    {renderM5AInputs(entry)}
                    {m5aMatrix}
                    {entry.notes && <div className="entry-notes"><strong>Notes:</strong> {entry.notes}</div>}
                  </div>
                );
              }

              if (isM5B) {
                const m5bMatrix = renderM5BOutputsMatrix(entry);
                const outs = entry.outputs || {};
                return (
                  <div key={index} className="entry-card entry-m5b">
                    <div className="entry-header">
                      <span className="entry-step">Step M5B</span>
                      <span className="entry-time">{formatTime(entry.timestamp)}</span>
                      {entry.duration_ms !== undefined && entry.duration_ms !== null && (
                        <span className="entry-duration">{entry.duration_ms.toFixed(1)}ms</span>
                      )}
                      <span className="entry-function-inline"><code>{m5bTitle}</code></span>
                    </div>
                    {renderM5BInputs(entry)}
                    <div className="m5b-summary">
                      <div><strong>Status:</strong> {String(outs['status'] ?? '—')}</div>
                      <div><strong>Candidate documents:</strong> {String(outs['stage_1_candidates'] ?? '—')}</div>
                      <div><strong>Total documents:</strong> {String(outs['total_documents'] ?? '—')}</div>
                      <div><strong>Returned (top_k after filters):</strong> {String(outs['candidates_found'] ?? '—')}</div>
                      <div><strong>Stage 1 time (ms):</strong> {String(outs['stage_1_time_ms'] ?? '—')}</div>
                      <div><strong>Stage 2 time (ms):</strong> {String(outs['stage_2_time_ms'] ?? '—')}</div>
                    </div>
                    {m5bMatrix}
                    {entry.notes && <div className="entry-notes"><strong>Notes:</strong> {entry.notes}</div>}
                  </div>
                );
              }

              return (
                <div key={index} className={`entry-card ${entry.function_name === 'result_document' ? 'entry-result' : ''} ${collapsed ? 'collapsed' : ''}`}>
                  <div className="entry-header">
                    <span className="entry-step">Step {entry.step_number}</span>
                    <span className="entry-time">{formatTime(entry.timestamp)}</span>
                    {entry.duration_ms !== undefined && entry.duration_ms !== null && (
                      <span className="entry-duration">{entry.duration_ms.toFixed(1)}ms</span>
                    )}
                    {collapsible && (
                      <button type="button" className="collapse-toggle" onClick={() => toggleCollapsed(entry.step_number)}
                        aria-label={collapsed ? 'Expand step' : 'Collapse step'} title={collapsed ? 'Expand' : 'Collapse'}>
                        {collapsed ? '▸' : '▾'}
                      </button>
                    )}
                    <span className="entry-function-inline"><code>{entry.function_name}</code></span>
                  </div>
                  {!collapsed && entry.description && <div className="entry-description">{entry.description}</div>}
                  {!collapsed && (
                    <div className="entry-details">
                      <div className="detail-section detail-section-wide">
                        <h4>📥 Inputs</h4>
                        {renderObject(entry.inputs)}
                      </div>
                      <div className="detail-section detail-section-narrow">
                        <h4>📤 Outputs</h4>
                        {renderObject(entry.outputs)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }

      {transcript.status === 'running' && (
        <div className="running-indicator">
          <div className="pulse" />
          <span>Search in progress...</span>
        </div>
      )}

      {transcript.status === 'completed' && transcript.final_result && transcript.final_result['type'] !== 'full_kb_review' && (
        <div className="final-result">
          <h3>Final Result</h3>
          {renderObject(transcript.final_result)}
        </div>
      )}

      {transcript.status === 'error' && transcript.error_message && (
        <div className="error-result">
          <h3>Error</h3>
          <p>{transcript.error_message}</p>
        </div>
      )}
    </main>
  );
}
